import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { FreeMode, Mousewheel, Scrollbar } from 'swiper/modules';
import type { UseFetchHandlerOptions } from '@mwl/core-lib';
import { getPinnedLinesSB, selectUserCurrency, selectUserLocale, useFetch, useToggle } from '@mwl/core-lib';
import ArrowIcon from '@public/assets/common/icons/arrowUpDouble.svg';
import TopIcon from '@public/assets/sport/icons/top.svg';
import { PinnedLine } from '@/components/PinnedLine/PinnedLine';
import type { SliderClasses } from '@/components/Slider/Slider';
import { Slider } from '@/components/Slider/Slider';
import { sportPinnedBreakpoints } from '@/constants';
import { useMediaWidthDown, useTypedSelector } from '@/hooks';
import { sportLinesEnrichment } from '@/utils';
import styles from './PinnedLines.module.scss';
const sliderClasses: SliderClasses = {
  slide: styles.slide,
  endShadow: styles.endShadow,
  startShadow: styles.startShadow
};
export const PinnedLines: FC = () => {
  const {
    t
  } = useTranslation('sport');
  const [expanded, toggleExpanded] = useToggle(true);
  const locale = useTypedSelector(selectUserLocale);
  const currency = useTypedSelector(selectUserCurrency);
  const isMobile = useMediaWidthDown('screen850');
  const fetchLines = useCallback(({
    signal
  }: UseFetchHandlerOptions) => getPinnedLinesSB({
    pageOffset: 0,
    pageSize: 10
  }, {
    signal
  }), []);
  const {
    data: linesResponse
  } = useFetch('pinnedLines', fetchLines, {
    disabled: !currency || !locale
  });
  const lines = useMemo(() => linesResponse ? sportLinesEnrichment(linesResponse) : [], [linesResponse]);
  if (!lines.length) {
    return null;
  }
  return <div className={styles.root} data-sentry-component="PinnedLines" data-sentry-source-file="PinnedLines.tsx">
      <button type="button" className={styles.header} onClick={() => toggleExpanded()}>
        <div className={styles.title}>
          <TopIcon className={styles.titleIcon} data-sentry-element="TopIcon" data-sentry-source-file="PinnedLines.tsx" />
          <h3 className={styles.titleText}>{t('top_events', 'Top events')}</h3>
          <span className={styles.titleCount}>{lines.length}</span>
        </div>
        <ArrowIcon className={cn(styles.arrow, {
        [styles.active]: expanded
      })} data-sentry-element="ArrowIcon" data-sentry-source-file="PinnedLines.tsx" />
      </button>
      {expanded && <Slider breakpoints={sportPinnedBreakpoints} isEndShadow={!isMobile} isStartShadow={!isMobile} classes={sliderClasses} className={styles.slider} modules={[Scrollbar, Mousewheel, FreeMode]} freeMode mousewheel={{
      forceToAxis: true
    }} scrollbar={{
      hide: false,
      draggable: true
    }} spaceBetween={8}>
          {lines.map(line => <PinnedLine key={line.id} line={line} className={styles.item} />)}
        </Slider>}
    </div>;
};